import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { ImageUpload } from "../ui/image-upload";
import LookbookManager from "./LookbookManager";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { toast } from "sonner";
import { 
  Calendar as CalendarIcon, 
  Plus, 
  Edit, 
  Trash2, 
  Send, 
  Clock, 
  CheckCircle, 
  XCircle, 
  Image as ImageIcon,
  Hash,
  FileText,
  Link2,
  Twitter,
  Facebook,
  Instagram,
  Linkedin,
  LayoutGrid,
  List,
  ChevronLeft,
  ChevronRight,
  Eye,
  MoreVertical,
  Copy,
  Sparkles,
  TrendingUp,
  Share2,
  Settings,
  Key,
  ExternalLink,
  AlertCircle,
  Unlink,
  Wand2,
  Loader2,
  Package
} from "lucide-react";
import { Calendar } from "../ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, addMonths, subMonths } from "date-fns";
import { API } from "../../apiConfig";


// Platform icons mapping
const PlatformIcon = ({ platform, className = "h-4 w-4" }) => {
  const icons = {
    twitter: <Twitter className={className} />,
    facebook: <Facebook className={className} />,
    instagram: <Instagram className={className} />,
    linkedin: <Linkedin className={className} />,
    pinterest: <Share2 className={className} />
  };
  return icons[platform] || null;
};

// Status badge component
const StatusBadge = ({ status }) => {
  const styles = {
    draft: "bg-gray-100 text-gray-800",
    scheduled: "bg-blue-100 text-blue-800",
    publishing: "bg-yellow-100 text-yellow-800",
    published: "bg-green-100 text-green-800",
    failed: "bg-red-100 text-red-800",
    cancelled: "bg-gray-100 text-gray-500"
  };
  
  const icons = {
    draft: <FileText className="h-3 w-3 mr-1" />,
    scheduled: <Clock className="h-3 w-3 mr-1" />,
    publishing: <Send className="h-3 w-3 mr-1 animate-pulse" />,
    published: <CheckCircle className="h-3 w-3 mr-1" />,
    failed: <XCircle className="h-3 w-3 mr-1" />,
    cancelled: <XCircle className="h-3 w-3 mr-1" />
  };
  
  return (
    <Badge className={`${styles[status]} flex items-center`}>
      {icons[status]}
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </Badge>
  );
};

const MarketingTab = ({ token }) => {
  const [view, setView] = useState("overview"); // overview, posts, calendar, settings
  const [posts, setPosts] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [stats, setStats] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [hashtagSets, setHashtagSets] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Post editor state
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [postForm, setPostForm] = useState({
    title: "",
    content: "",
    platforms: [],
    images: [],
    product_ids: [],
    hashtags: [],
    scheduled_for: null,
    auto_publish: true
  });
  
  // Platform connection state
  const [connectDialogOpen, setConnectDialogOpen] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [credentialsForm, setCredentialsForm] = useState({});
  const [savingCredentials, setSavingCredentials] = useState(false);
  
  // AI Generation state
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState(null);
  
  // Calendar state
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [calendarPosts, setCalendarPosts] = useState([]);
  
  // Fetch initial data
  useEffect(() => {
    fetchData();
  }, [token]);
  
  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [platformsRes, statsRes, postsRes, templatesRes, hashtagsRes, productsRes] = await Promise.all([
        axios.get(`${API}/marketing/platforms`, { headers }),
        axios.get(`${API}/marketing/stats`, { headers }),
        axios.get(`${API}/marketing/posts?limit=50`, { headers }),
        axios.get(`${API}/marketing/templates`, { headers }),
        axios.get(`${API}/marketing/hashtags`, { headers }),
        axios.get(`${API}/products`, { headers })
      ]);
      
      setPlatforms(platformsRes.data.platforms || []);
      setStats(statsRes.data);
      setPosts(postsRes.data.posts || []);
      setTemplates(templatesRes.data.templates || []);
      setHashtagSets(hashtagsRes.data.hashtag_sets || []);
      setProducts(productsRes.data.products || []);
    } catch (error) {
      console.error("Error fetching marketing data:", error);
      toast.error("Failed to load marketing data");
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch calendar posts for current month
  useEffect(() => {
    if (view === "calendar") {
      fetchCalendarPosts();
    }
  }, [view, calendarDate]);
  
  const fetchCalendarPosts = async () => {
    try {
      const start = startOfMonth(calendarDate);
      const end = endOfMonth(calendarDate);
      
      const res = await axios.get(`${API}/marketing/posts/calendar`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          start_date: start.toISOString(),
          end_date: end.toISOString()
        }
      });
      
      setCalendarPosts(res.data.posts || []);
    } catch (error) {
      console.error("Error fetching calendar posts:", error);
    }
  };
  
  // Post CRUD operations
  const handleCreatePost = async () => {
    try {
      const data = {
        ...postForm,
        scheduled_for: postForm.scheduled_for ? new Date(postForm.scheduled_for).toISOString() : null
      };
      
      const res = await axios.post(`${API}/marketing/posts`, data, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setPosts([res.data.post, ...posts]);
      setEditorOpen(false);
      resetPostForm();
      toast.success("Post created successfully");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create post");
    }
  };
  
  const handleUpdatePost = async () => {
    try {
      const data = {
        ...postForm,
        scheduled_for: postForm.scheduled_for ? new Date(postForm.scheduled_for).toISOString() : null
      };
      
      await axios.put(`${API}/marketing/posts/${editingPost.post_id}`, data, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Post updated successfully");
      setEditorOpen(false);
      resetPostForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update post");
    }
  };
  
  const handleDeletePost = async (postId) => {
    if (!window.confirm("Are you sure you want to delete this post?")) return;
    
    try {
      await axios.delete(`${API}/marketing/posts/${postId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setPosts(posts.filter(p => p.post_id !== postId));
      toast.success("Post deleted");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete post");
    }
  };
  
  const handlePublishPost = async (postId) => {
    try {
      await axios.post(`${API}/marketing/posts/${postId}/publish`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Post published successfully!");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to publish post");
    }
  };
  
  const resetPostForm = () => {
    setPostForm({
      title: "",
      content: "",
      platforms: [],
      images: [],
      product_ids: [],
      hashtags: [],
      scheduled_for: null,
      auto_publish: true
    });
    setEditingPost(null);
  };
  
  const openEditor = (post = null) => {
    if (post) {
      setEditingPost(post);
      setPostForm({
        title: post.title || "",
        content: post.content || "",
        platforms: post.platforms || [],
        images: post.images || [],
        product_ids: post.product_ids || [],
        hashtags: post.hashtags || [],
        scheduled_for: post.scheduled_for || null,
        auto_publish: post.auto_publish !== false
      });
    } else {
      resetPostForm();
    }
    setEditorOpen(true);
  };
  
  const togglePlatform = (platform) => {
    setPostForm(prev => ({
      ...prev,
      platforms: prev.platforms.includes(platform)
        ? prev.platforms.filter(p => p !== platform)
        : [...prev.platforms, platform]
    }));
  };
  
  const addHashtag = (tag) => {
    const cleanTag = tag.startsWith("#") ? tag : `#${tag}`;
    if (!postForm.hashtags.includes(cleanTag)) {
      setPostForm(prev => ({
        ...prev,
        hashtags: [...prev.hashtags, cleanTag]
      }));
    }
  };
  
  const removeHashtag = (tag) => {
    setPostForm(prev => ({
      ...prev,
      hashtags: prev.hashtags.filter(t => t !== tag)
    }));
  };
  
  // Get character count for selected platforms
  const getCharLimit = () => {
    const limits = platforms
      .filter(p => postForm.platforms.includes(p.platform))
      .map(p => p.max_chars);
    return limits.length ? Math.min(...limits) : 280;
  };
  
  // AI Caption Generation
  const generateAICaption = async () => {
    if (!selectedProduct) {
      toast.error("Please select a product first");
      return;
    }
    
    setAiGenerating(true);
    setAiSuggestions(null);
    
    try {
      const res = await axios.post(`${API}/ai/quick`, {
        action: "generate_caption",
        context: {
          name: selectedProduct.name,
          artist: selectedProduct.artist_name || "Colin Isaacs",
          price: selectedProduct.price,
          description: selectedProduct.description,
          category: selectedProduct.category
        }
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setAiSuggestions({
        type: "caption",
        content: res.data.result
      });
      toast.success("Captions generated!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate caption");
    } finally {
      setAiGenerating(false);
    }
  };
  
  const generateAIHashtags = async () => {
    setAiGenerating(true);
    
    try {
      const context = selectedProduct ? {
        name: selectedProduct.name,
        artist: selectedProduct.artist_name || "Colin Isaacs",
        category: selectedProduct.category
      } : {};
      
      const res = await axios.post(`${API}/ai/quick`, {
        action: "generate_hashtags",
        context
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setAiSuggestions({
        type: "hashtags",
        content: res.data.result
      });
      toast.success("Hashtags generated!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate hashtags");
    } finally {
      setAiGenerating(false);
    }
  };
  
  const applyAISuggestion = (text) => {
    setPostForm(prev => ({
      ...prev,
      content: text
    }));
    toast.success("Caption applied!");
  };
  
  const extractHashtagsFromAI = () => {
    if (!aiSuggestions?.content) return;
    
    // Extract hashtags from the AI response
    const hashtagRegex = /#[\w]+/g;
    const foundHashtags = aiSuggestions.content.match(hashtagRegex) || [];
    const uniqueHashtags = [...new Set(foundHashtags)];
    
    setPostForm(prev => ({
      ...prev,
      hashtags: [...new Set([...prev.hashtags, ...uniqueHashtags])]
    }));
    toast.success(`Added ${uniqueHashtags.length} hashtags!`);
  };
  
  // Platform credentials configuration
  const PLATFORM_CREDENTIALS = {
    twitter: {
      name: "Twitter/X",
      fields: [
        { key: "api_key", label: "API Key (Consumer Key)", placeholder: "Enter your Twitter API Key", type: "text" },
        { key: "api_secret", label: "API Secret (Consumer Secret)", placeholder: "Enter your Twitter API Secret", type: "password" },
        { key: "access_token", label: "Access Token", placeholder: "Enter your Access Token", type: "text" },
        { key: "access_token_secret", label: "Access Token Secret", placeholder: "Enter your Access Token Secret", type: "password" }
      ],
      docsUrl: "https://developer.twitter.com/en/portal/dashboard",
      instructions: "Create an app at developer.twitter.com, enable OAuth 1.0a, and generate your API keys and access tokens."
    },
    facebook: {
      name: "Facebook",
      fields: [
        { key: "app_id", label: "App ID", placeholder: "Enter your Facebook App ID", type: "text" },
        { key: "app_secret", label: "App Secret", placeholder: "Enter your Facebook App Secret", type: "password" },
        { key: "page_id", label: "Page ID", placeholder: "Enter your Facebook Page ID", type: "text" },
        { key: "page_access_token", label: "Page Access Token", placeholder: "Enter your Page Access Token", type: "password" }
      ],
      docsUrl: "https://developers.facebook.com/apps/",
      instructions: "Create an app at developers.facebook.com, add Facebook Login product, and generate a Page Access Token with pages_manage_posts permission."
    },
    instagram: {
      name: "Instagram",
      fields: [
        { key: "app_id", label: "App ID (same as Facebook)", placeholder: "Enter your Facebook App ID", type: "text" },
        { key: "app_secret", label: "App Secret (same as Facebook)", placeholder: "Enter your Facebook App Secret", type: "password" },
        { key: "instagram_account_id", label: "Instagram Business Account ID", placeholder: "Enter your Instagram Business Account ID", type: "text" },
        { key: "access_token", label: "Access Token", placeholder: "Enter your Access Token", type: "password" }
      ],
      docsUrl: "https://developers.facebook.com/docs/instagram-api/getting-started",
      instructions: "Instagram API uses Meta Business Suite. Your Instagram account must be a Business account linked to a Facebook Page."
    },
    linkedin: {
      name: "LinkedIn",
      fields: [
        { key: "client_id", label: "Client ID", placeholder: "Enter your LinkedIn Client ID", type: "text" },
        { key: "client_secret", label: "Client Secret", placeholder: "Enter your LinkedIn Client Secret", type: "password" },
        { key: "organization_id", label: "Organization ID (optional)", placeholder: "For company pages", type: "text" }
      ],
      docsUrl: "https://www.linkedin.com/developers/apps",
      instructions: "Create an app at linkedin.com/developers, request w_member_social permission for posting. Add organization ID for company page posts."
    },
    pinterest: {
      name: "Pinterest",
      fields: [
        { key: "app_id", label: "App ID", placeholder: "Enter your Pinterest App ID", type: "text" },
        { key: "app_secret", label: "App Secret", placeholder: "Enter your Pinterest App Secret", type: "password" },
        { key: "access_token", label: "Access Token", placeholder: "Enter your Access Token", type: "password" }
      ],
      docsUrl: "https://developers.pinterest.com/apps/",
      instructions: "Create an app at developers.pinterest.com, request pins:read and pins:write scopes, and generate an access token."
    }
  };
  
  // Open platform connection dialog
  const openConnectDialog = (platform) => {
    setSelectedPlatform(platform);
    // Initialize form with existing credentials if any
    const existingPlatform = platforms.find(p => p.platform === platform);
    const initialForm = {};
    PLATFORM_CREDENTIALS[platform]?.fields.forEach(field => {
      initialForm[field.key] = existingPlatform?.credentials?.[field.key] || "";
    });
    setCredentialsForm(initialForm);
    setConnectDialogOpen(true);
  };
  
  // Save platform credentials
  const saveCredentials = async () => {
    if (!selectedPlatform) return;
    
    setSavingCredentials(true);
    try {
      const platformConfig = PLATFORM_CREDENTIALS[selectedPlatform];
      const accountName = credentialsForm.page_id || credentialsForm.organization_id || credentialsForm.instagram_account_id || `@${selectedPlatform}`;
      
      await axios.post(`${API}/marketing/platforms/${selectedPlatform}/connect`, {
        access_token: credentialsForm.access_token || credentialsForm.page_access_token || "configured",
        refresh_token: credentialsForm.refresh_token,
        account_name: accountName,
        account_id: credentialsForm.page_id || credentialsForm.instagram_account_id || credentialsForm.organization_id,
        credentials: credentialsForm
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`${platformConfig.name} connected successfully!`);
      setConnectDialogOpen(false);
      setSelectedPlatform(null);
      setCredentialsForm({});
      fetchData(); // Refresh platforms
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save credentials");
    } finally {
      setSavingCredentials(false);
    }
  };
  
  // Disconnect platform
  const disconnectPlatform = async (platform) => {
    if (!window.confirm(`Are you sure you want to disconnect ${PLATFORM_CREDENTIALS[platform]?.name}?`)) return;
    
    try {
      await axios.delete(`${API}/marketing/platforms/${platform}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Platform disconnected");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to disconnect platform");
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Lookbook management (private) */}
      <LookbookManager token={token} />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold">Content Marketing</h2>
          <p className="text-muted-foreground">Create, schedule and publish social media content</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant={view === "overview" ? "default" : "outline"}
            size="sm"
            onClick={() => setView("overview")}
          >
            <TrendingUp className="h-4 w-4 mr-2" />
            Overview
          </Button>
          <Button
            variant={view === "posts" ? "default" : "outline"}
            size="sm"
            onClick={() => setView("posts")}
          >
            <List className="h-4 w-4 mr-2" />
            Posts
          </Button>
          <Button
            variant={view === "calendar" ? "default" : "outline"}
            size="sm"
            onClick={() => setView("calendar")}
          >
            <CalendarIcon className="h-4 w-4 mr-2" />
            Calendar
          </Button>
          <Button
            variant={view === "settings" ? "default" : "outline"}
            size="sm"
            onClick={() => setView("settings")}
          >
            <Settings className="h-4 w-4 mr-2" />
            Settings
          </Button>
          <Button onClick={() => openEditor()} className="ml-2">
            <Plus className="h-4 w-4 mr-2" />
            New Post
          </Button>
        </div>
      </div>
      
      {/* Overview View */}
      {view === "overview" && (
        <div className="space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Posts</p>
                    <p className="text-2xl font-bold">{stats?.total_posts || 0}</p>
                  </div>
                  <FileText className="h-8 w-8 text-muted-foreground/50" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Drafts</p>
                    <p className="text-2xl font-bold">{stats?.draft_posts || 0}</p>
                  </div>
                  <Edit className="h-8 w-8 text-muted-foreground/50" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Scheduled</p>
                    <p className="text-2xl font-bold">{stats?.scheduled_posts || 0}</p>
                  </div>
                  <Clock className="h-8 w-8 text-blue-500/50" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Published</p>
                    <p className="text-2xl font-bold">{stats?.published_posts || 0}</p>
                  </div>
                  <CheckCircle className="h-8 w-8 text-green-500/50" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Connected</p>
                    <p className="text-2xl font-bold">{stats?.connected_platforms || 0}/5</p>
                  </div>
                  <Share2 className="h-8 w-8 text-primary/50" />
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Connected Platforms */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Share2 className="h-5 w-5" />
                  Connected Platforms
                </CardTitle>
                <Button variant="outline" size="sm" onClick={() => setView("settings")}>
                  <Settings className="h-4 w-4 mr-2" />
                  Manage Connections
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {platforms.map((platform) => (
                  <div
                    key={platform.platform}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      platform.is_connected 
                        ? "border-green-500 bg-green-50" 
                        : "border-dashed border-muted-foreground/25 hover:border-primary/50"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-full ${platform.is_connected ? "bg-green-100" : "bg-muted"}`}>
                        <PlatformIcon platform={platform.platform} className="h-5 w-5" />
                      </div>
                      <div>
                        <p className="font-medium">{platform.name}</p>
                        {platform.is_connected ? (
                          <p className="text-xs text-green-600">Connected</p>
                        ) : (
                          <p className="text-xs text-muted-foreground">Not connected</p>
                        )}
                      </div>
                    </div>
                    {!platform.is_connected ? (
                      <Button variant="outline" size="sm" className="w-full mt-3" onClick={() => openConnectDialog(platform.platform)}>
                        <Key className="h-3 w-3 mr-1" />
                        Connect
                      </Button>
                    ) : (
                      <Button variant="ghost" size="sm" className="w-full mt-3 text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => disconnectPlatform(platform.platform)}>
                        <Unlink className="h-3 w-3 mr-1" />
                        Disconnect
                      </Button>
                    )}
                  </div>
                ))}
              </div>
              <p className="text-sm text-muted-foreground mt-4">
                💡 Click &quot;Connect&quot; to enter your API credentials, or go to Settings to manage all connections.
              </p>
            </CardContent>
          </Card>
          
          {/* Recent & Upcoming */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Upcoming Scheduled */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  Upcoming Scheduled
                </CardTitle>
              </CardHeader>
              <CardContent>
                {stats?.upcoming_posts?.length > 0 ? (
                  <div className="space-y-3">
                    {stats.upcoming_posts.map((post) => (
                      <div key={post.post_id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{post.title || post.content?.slice(0, 50)}...</p>
                          <div className="flex items-center gap-2 mt-1">
                            {post.platforms?.map(p => (
                              <PlatformIcon key={p} platform={p} className="h-3 w-3 text-muted-foreground" />
                            ))}
                            <span className="text-xs text-muted-foreground">
                              {post.scheduled_for && format(new Date(post.scheduled_for), "MMM d, h:mm a")}
                            </span>
                          </div>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => openEditor(post)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-6">No scheduled posts</p>
                )}
              </CardContent>
            </Card>
            
            {/* Recently Published */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5" />
                  Recently Published
                </CardTitle>
              </CardHeader>
              <CardContent>
                {stats?.recent_posts?.length > 0 ? (
                  <div className="space-y-3">
                    {stats.recent_posts.map((post) => (
                      <div key={post.post_id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{post.title || post.content?.slice(0, 50)}...</p>
                          <div className="flex items-center gap-2 mt-1">
                            {post.platforms?.map(p => (
                              <PlatformIcon key={p} platform={p} className="h-3 w-3 text-muted-foreground" />
                            ))}
                            <span className="text-xs text-muted-foreground">
                              {post.published_at && format(new Date(post.published_at), "MMM d, h:mm a")}
                            </span>
                          </div>
                        </div>
                        <StatusBadge status={post.status} />
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-6">No published posts yet</p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      )}
      
      {/* Posts List View */}
      {view === "posts" && (
        <Card>
          <CardHeader>
            <CardTitle>All Posts</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Post</TableHead>
                  <TableHead>Platforms</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Scheduled</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {posts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                      No posts yet. Create your first post!
                    </TableCell>
                  </TableRow>
                ) : (
                  posts.map((post) => (
                    <TableRow key={post.post_id}>
                      <TableCell>
                        <div className="max-w-xs">
                          <p className="font-medium">{post.title || "Untitled"}</p>
                          <p className="text-sm text-muted-foreground truncate">{post.content?.slice(0, 60)}...</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {post.platforms?.map(p => (
                            <PlatformIcon key={p} platform={p} className="h-4 w-4 text-muted-foreground" />
                          ))}
                        </div>
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={post.status} />
                      </TableCell>
                      <TableCell>
                        {post.scheduled_for 
                          ? format(new Date(post.scheduled_for), "MMM d, yyyy h:mm a")
                          : "-"
                        }
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {post.status === "draft" && (
                            <Button variant="ghost" size="sm" onClick={() => handlePublishPost(post.post_id)}>
                              <Send className="h-4 w-4" />
                            </Button>
                          )}
                          {post.status !== "published" && (
                            <Button variant="ghost" size="sm" onClick={() => openEditor(post)}>
                              <Edit className="h-4 w-4" />
                            </Button>
                          )}
                          <Button variant="ghost" size="sm" onClick={() => handleDeletePost(post.post_id)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
      
      {/* Calendar View */}
      {view === "calendar" && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Content Calendar</CardTitle>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setCalendarDate(subMonths(calendarDate, 1))}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="font-medium min-w-[140px] text-center">
                  {format(calendarDate, "MMMM yyyy")}
                </span>
                <Button variant="outline" size="sm" onClick={() => setCalendarDate(addMonths(calendarDate, 1))}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-7 gap-px bg-muted rounded-lg overflow-hidden">
              {/* Day headers */}
              {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(day => (
                <div key={day} className="bg-background p-2 text-center text-sm font-medium">
                  {day}
                </div>
              ))}
              
              {/* Calendar days */}
              {(() => {
                const start = startOfMonth(calendarDate);
                const end = endOfMonth(calendarDate);
                const days = eachDayOfInterval({ start, end });
                
                // Pad start with empty cells
                const startDay = start.getDay();
                const paddedDays = [...Array(startDay).fill(null), ...days];
                
                return paddedDays.map((day, idx) => {
                  if (!day) {
                    return <div key={`empty-${idx}`} className="bg-background p-2 min-h-[100px]" />;
                  }
                  
                  const dayPosts = calendarPosts.filter(p => 
                    p.scheduled_for && isSameDay(new Date(p.scheduled_for), day)
                  );
                  
                  return (
                    <div 
                      key={day.toISOString()} 
                      className={`bg-background p-2 min-h-[100px] ${
                        isSameDay(day, new Date()) ? "ring-2 ring-primary ring-inset" : ""
                      }`}
                    >
                      <p className="text-sm font-medium mb-1">{format(day, "d")}</p>
                      <div className="space-y-1">
                        {dayPosts.slice(0, 3).map(post => (
                          <div 
                            key={post.post_id}
                            className={`text-xs p-1 rounded truncate cursor-pointer ${
                              post.status === "published" ? "bg-green-100 text-green-800" :
                              post.status === "scheduled" ? "bg-blue-100 text-blue-800" :
                              "bg-gray-100 text-gray-800"
                            }`}
                            onClick={() => openEditor(post)}
                          >
                            {post.title || post.content?.slice(0, 20)}
                          </div>
                        ))}
                        {dayPosts.length > 3 && (
                          <p className="text-xs text-muted-foreground">+{dayPosts.length - 3} more</p>
                        )}
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Post Editor Dialog */}
      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingPost ? "Edit Post" : "Create New Post"}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Title */}
            <div className="space-y-2">
              <Label>Title (Internal Reference)</Label>
              <Input
                placeholder="e.g., New Artwork Launch"
                value={postForm.title}
                onChange={(e) => setPostForm({ ...postForm, title: e.target.value })}
              />
            </div>
            
            {/* Platform Selection */}
            <div className="space-y-2">
              <Label>Platforms</Label>
              <div className="flex flex-wrap gap-2">
                {platforms.map((platform) => (
                  <Button
                    key={platform.platform}
                    type="button"
                    variant={postForm.platforms.includes(platform.platform) ? "default" : "outline"}
                    size="sm"
                    onClick={() => togglePlatform(platform.platform)}
                    disabled={!platform.is_connected}
                    className="gap-2"
                  >
                    <PlatformIcon platform={platform.platform} />
                    {platform.name}
                    {!platform.is_connected && <span className="text-xs">(Not connected)</span>}
                  </Button>
                ))}
              </div>
            </div>
            
            {/* AI-Powered Content Generation */}
            <div className="space-y-3 p-4 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-2">
                  <Wand2 className="h-4 w-4 text-amber-600" />
                  AI Content Generator
                </Label>
              </div>
              
              {/* Product Selection */}
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Select product to generate content about:</Label>
                <Select
                  value={selectedProduct?.product_id || ""}
                  onValueChange={(id) => {
                    const product = products.find(p => p.product_id === id);
                    setSelectedProduct(product);
                    if (product?.images?.[0]) {
                      setPostForm(prev => ({ ...prev, images: [product.images[0]] }));
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a product..." />
                  </SelectTrigger>
                  <SelectContent>
                    {products.map((product) => (
                      <SelectItem key={product.product_id} value={product.product_id}>
                        <div className="flex items-center gap-2">
                          {product.images?.[0] && (
                            <img src={product.images[0]} alt="" className="w-6 h-6 object-cover rounded" />
                          )}
                          {product.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* AI Generation Buttons */}
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={generateAICaption}
                  disabled={aiGenerating || !selectedProduct}
                  className="flex-1"
                >
                  {aiGenerating ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Sparkles className="h-4 w-4 mr-2" />
                  )}
                  Generate Caption
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={generateAIHashtags}
                  disabled={aiGenerating}
                  className="flex-1"
                >
                  {aiGenerating ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Hash className="h-4 w-4 mr-2" />
                  )}
                  Generate Hashtags
                </Button>
              </div>
              
              {/* AI Suggestions Display */}
              {aiSuggestions && (
                <div className="mt-3 p-3 bg-white border rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-xs font-medium">
                      {aiSuggestions.type === "caption" ? "Generated Captions" : "Generated Hashtags"}
                    </Label>
                    <div className="flex gap-1">
                      {aiSuggestions.type === "caption" && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-6 text-xs"
                          onClick={() => applyAISuggestion(aiSuggestions.content.split("###")[1]?.split("\n").slice(1, 4).join("\n").trim() || aiSuggestions.content.slice(0, 280))}
                        >
                          Use First
                        </Button>
                      )}
                      {aiSuggestions.type === "hashtags" && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-6 text-xs"
                          onClick={extractHashtagsFromAI}
                        >
                          Add All Hashtags
                        </Button>
                      )}
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground max-h-32 overflow-y-auto whitespace-pre-wrap">
                    {aiSuggestions.content}
                  </div>
                </div>
              )}
            </div>
            
            {/* Content */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Content</Label>
                <span className={`text-xs ${postForm.content.length > getCharLimit() ? "text-red-500" : "text-muted-foreground"}`}>
                  {postForm.content.length} / {getCharLimit()} characters
                </span>
              </div>
              <Textarea
                placeholder="What would you like to share? Use AI generator above to create content..."
                value={postForm.content}
                onChange={(e) => setPostForm({ ...postForm, content: e.target.value })}
                rows={5}
                className="resize-none"
              />
            </div>
            
            {/* Hashtags */}
            <div className="space-y-2">
              <Label>Hashtags</Label>
              <div className="flex flex-wrap gap-2 mb-2">
                {postForm.hashtags.map((tag) => (
                  <Badge key={tag} variant="secondary" className="gap-1">
                    {tag}
                    <button onClick={() => removeHashtag(tag)} className="ml-1 hover:text-red-500">
                      ×
                    </button>
                  </Badge>
                ))}
              </div>
              <div className="flex gap-2">
                <Input
                  placeholder="Add hashtag..."
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addHashtag(e.target.value);
                      e.target.value = "";
                    }
                  }}
                />
                <Button variant="outline" type="button" onClick={() => {
                  // Quick add common hashtags
                  ["AboriginalArt", "IndigenousArt", "AustralianArt", "ArawolGallery", "FirstNationsArt"].forEach(addHashtag);
                }}>
                  <Hash className="h-4 w-4 mr-2" />
                  Quick Add
                </Button>
              </div>
            </div>
            
            {/* Images */}
            <div className="space-y-2">
              <Label>Images</Label>
              {selectedProduct?.images?.[0] && (
                <div className="flex items-center gap-2 p-2 bg-muted/50 rounded-lg mb-2">
                  <img src={selectedProduct.images[0]} alt="" className="w-12 h-12 object-cover rounded" />
                  <div className="flex-1">
                    <p className="text-sm font-medium">{selectedProduct.name}</p>
                    <p className="text-xs text-muted-foreground">Product image selected</p>
                  </div>
                </div>
              )}
              <ImageUpload
                value={postForm.images[0]}
                onChange={(url) => setPostForm({ ...postForm, images: url ? [url] : [] })}
                onRemove={() => setPostForm({ ...postForm, images: [] })}
                label="Upload or drag image"
              />
              {postForm.images.length > 0 && (
                <p className="text-xs text-muted-foreground">
                  {postForm.images.length} image(s) attached
                </p>
              )}
            </div>
            
            {/* Scheduling */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label>Schedule Post</Label>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={postForm.auto_publish}
                    onCheckedChange={(checked) => setPostForm({ ...postForm, auto_publish: checked })}
                  />
                  <span className="text-sm text-muted-foreground">
                    {postForm.auto_publish ? "Auto-publish" : "Manual confirmation"}
                  </span>
                </div>
              </div>
              
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start">
                    <CalendarIcon className="h-4 w-4 mr-2" />
                    {postForm.scheduled_for 
                      ? format(new Date(postForm.scheduled_for), "PPP p")
                      : "Select date and time"
                    }
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={postForm.scheduled_for ? new Date(postForm.scheduled_for) : undefined}
                    onSelect={(date) => {
                      if (date) {
                        // Keep existing time or default to noon
                        const existing = postForm.scheduled_for ? new Date(postForm.scheduled_for) : new Date();
                        date.setHours(existing.getHours() || 12, existing.getMinutes() || 0);
                        setPostForm({ ...postForm, scheduled_for: date.toISOString() });
                      }
                    }}
                    disabled={(date) => date < new Date()}
                  />
                  <div className="p-3 border-t">
                    <Label className="text-xs">Time</Label>
                    <Input
                      type="time"
                      value={postForm.scheduled_for ? format(new Date(postForm.scheduled_for), "HH:mm") : "12:00"}
                      onChange={(e) => {
                        const [hours, minutes] = e.target.value.split(":");
                        const date = postForm.scheduled_for ? new Date(postForm.scheduled_for) : new Date();
                        date.setHours(parseInt(hours), parseInt(minutes));
                        setPostForm({ ...postForm, scheduled_for: date.toISOString() });
                      }}
                    />
                  </div>
                </PopoverContent>
              </Popover>
              
              {postForm.scheduled_for && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setPostForm({ ...postForm, scheduled_for: null })}
                >
                  Clear schedule (save as draft)
                </Button>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditorOpen(false)}>
              Cancel
            </Button>
            {!editingPost && (
              <Button 
                variant="outline"
                onClick={handleCreatePost}
                disabled={!postForm.content || postForm.platforms.length === 0}
              >
                Save as Draft
              </Button>
            )}
            <Button 
              onClick={editingPost ? handleUpdatePost : handleCreatePost}
              disabled={!postForm.content || postForm.platforms.length === 0}
            >
              {editingPost ? "Update Post" : (postForm.scheduled_for ? "Schedule Post" : "Save Post")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Platform Connection Dialog */}
      <Dialog open={connectDialogOpen} onOpenChange={setConnectDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PlatformIcon platform={selectedPlatform} className="h-5 w-5" />
              Connect {PLATFORM_CREDENTIALS[selectedPlatform]?.name}
            </DialogTitle>
          </DialogHeader>
          
          {selectedPlatform && PLATFORM_CREDENTIALS[selectedPlatform] && (
            <div className="space-y-4 py-4">
              {/* Instructions */}
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 text-blue-600 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-blue-800">Setup Instructions</p>
                    <p className="text-blue-700 mt-1">{PLATFORM_CREDENTIALS[selectedPlatform].instructions}</p>
                    <a 
                      href={PLATFORM_CREDENTIALS[selectedPlatform].docsUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-blue-600 hover:underline mt-2"
                    >
                      <ExternalLink className="h-3 w-3" />
                      Open Developer Portal
                    </a>
                  </div>
                </div>
              </div>
              
              {/* Credential Fields */}
              {PLATFORM_CREDENTIALS[selectedPlatform].fields.map((field) => (
                <div key={field.key} className="space-y-2">
                  <Label htmlFor={field.key}>{field.label}</Label>
                  <Input
                    id={field.key}
                    type={field.type}
                    placeholder={field.placeholder}
                    value={credentialsForm[field.key] || ""}
                    onChange={(e) => setCredentialsForm({
                      ...credentialsForm,
                      [field.key]: e.target.value
                    })}
                  />
                </div>
              ))}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setConnectDialogOpen(false);
              setSelectedPlatform(null);
              setCredentialsForm({});
            }}>
              Cancel
            </Button>
            <Button onClick={saveCredentials} disabled={savingCredentials}>
              {savingCredentials ? "Connecting..." : "Connect Platform"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Settings View */}
      {view === "settings" && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Key className="h-5 w-5" />
                Platform API Credentials
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Connect your social media accounts by entering your API credentials below.
              </p>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {Object.entries(PLATFORM_CREDENTIALS).map(([key, config]) => {
                  const platformData = platforms.find(p => p.platform === key);
                  const isConnected = platformData?.is_connected;
                  
                  return (
                    <div 
                      key={key}
                      className={`p-4 border rounded-lg ${isConnected ? "border-green-300 bg-green-50" : "border-muted"}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-full ${isConnected ? "bg-green-100" : "bg-muted"}`}>
                            <PlatformIcon platform={key} className="h-5 w-5" />
                          </div>
                          <div>
                            <p className="font-medium">{config.name}</p>
                            {isConnected ? (
                              <p className="text-xs text-green-600 flex items-center gap-1">
                                <CheckCircle className="h-3 w-3" />
                                Connected {platformData?.account_name ? `as ${platformData.account_name}` : ""}
                              </p>
                            ) : (
                              <p className="text-xs text-muted-foreground">Not connected</p>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          {isConnected ? (
                            <>
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => openConnectDialog(key)}
                              >
                                <Edit className="h-4 w-4 mr-1" />
                                Update
                              </Button>
                              <Button 
                                variant="outline" 
                                size="sm"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => disconnectPlatform(key)}
                              >
                                <Unlink className="h-4 w-4 mr-1" />
                                Disconnect
                              </Button>
                            </>
                          ) : (
                            <Button 
                              variant="default" 
                              size="sm"
                              onClick={() => openConnectDialog(key)}
                            >
                              <Key className="h-4 w-4 mr-1" />
                              Connect
                            </Button>
                          )}
                        </div>
                      </div>
                      
                      {/* Show what credentials are needed */}
                      {!isConnected && (
                        <div className="mt-3 pt-3 border-t">
                          <p className="text-xs text-muted-foreground mb-2">Required credentials:</p>
                          <div className="flex flex-wrap gap-1">
                            {config.fields.map(field => (
                              <Badge key={field.key} variant="outline" className="text-xs">
                                {field.label.split(" (")[0]}
                              </Badge>
                            ))}
                          </div>
                          <a 
                            href={config.docsUrl} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-primary hover:underline mt-2"
                          >
                            <ExternalLink className="h-3 w-3" />
                            Get credentials from {config.name}
                          </a>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
          
          {/* Help Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5" />
                Need Help?
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="p-4 border rounded-lg">
                  <h4 className="font-medium mb-2">Twitter/X Setup</h4>
                  <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
                    <li>Go to developer.twitter.com</li>
                    <li>Create a new project and app</li>
                    <li>Enable OAuth 1.0a User Context</li>
                    <li>Generate API Key, Secret, and Access Tokens</li>
                  </ol>
                </div>
                <div className="p-4 border rounded-lg">
                  <h4 className="font-medium mb-2">Meta (Facebook/Instagram) Setup</h4>
                  <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
                    <li>Go to developers.facebook.com</li>
                    <li>Create a new app (Business type)</li>
                    <li>Add Facebook Login and Instagram Graph API</li>
                    <li>Generate Page Access Token with required permissions</li>
                  </ol>
                </div>
                <div className="p-4 border rounded-lg">
                  <h4 className="font-medium mb-2">LinkedIn Setup</h4>
                  <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
                    <li>Go to linkedin.com/developers</li>
                    <li>Create a new app</li>
                    <li>Request w_member_social scope</li>
                    <li>Copy Client ID and Secret</li>
                  </ol>
                </div>
                <div className="p-4 border rounded-lg">
                  <h4 className="font-medium mb-2">Pinterest Setup</h4>
                  <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
                    <li>Go to developers.pinterest.com</li>
                    <li>Create a new app</li>
                    <li>Request pins:read and pins:write scopes</li>
                    <li>Generate Access Token</li>
                  </ol>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default MarketingTab;
